import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ToolPage from '@/components/ToolPage';
import { Field, inputClass } from '@/components/calcUi';
import { getToolBySlug } from '@/lib/tools';
import { formatNumber } from '@/lib/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const num = (v) => (v === '' || isNaN(Number(v)) ? NaN : Number(v));

// ========================= Unit Converter =========================
const UNITS = {
  length: {
    label: 'Length',
    base: 'm',
    items: [
      { code: 'mm', label: 'millimeter (mm)', to: 0.001 },
      { code: 'cm', label: 'centimeter (cm)', to: 0.01 },
      { code: 'm', label: 'meter (m)', to: 1 },
      { code: 'km', label: 'kilometer (km)', to: 1000 },
      { code: 'in', label: 'inch (in)', to: 0.0254 },
      { code: 'ft', label: 'foot (ft)', to: 0.3048 },
      { code: 'yd', label: 'yard (yd)', to: 0.9144 },
      { code: 'mi', label: 'mile (mi)', to: 1609.344 },
    ],
  },
  weight: {
    label: 'Weight',
    base: 'kg',
    items: [
      { code: 'mg', label: 'milligram (mg)', to: 0.000001 },
      { code: 'g', label: 'gram (g)', to: 0.001 },
      { code: 'kg', label: 'kilogram (kg)', to: 1 },
      { code: 't', label: 'metric ton (t)', to: 1000 },
      { code: 'oz', label: 'ounce (oz)', to: 0.0283495 },
      { code: 'lb', label: 'pound (lb)', to: 0.453592 },
    ],
  },
  volume: {
    label: 'Volume',
    base: 'L',
    items: [
      { code: 'mL', label: 'milliliter (mL)', to: 0.001 },
      { code: 'L', label: 'liter (L)', to: 1 },
      { code: 'm3', label: 'cubic meter (m³)', to: 1000 },
      { code: 'tsp', label: 'US teaspoon', to: 0.00492892 },
      { code: 'tbsp', label: 'US tablespoon', to: 0.0147868 },
      { code: 'floz', label: 'US fluid ounce', to: 0.0295735 },
      { code: 'cup', label: 'US cup', to: 0.24 },
      { code: 'pt', label: 'US pint', to: 0.473176 },
      { code: 'qt', label: 'US quart', to: 0.946353 },
      { code: 'gal', label: 'US gallon', to: 3.78541 },
    ],
  },
  temperature: {
    label: 'Temperature',
    items: [
      { code: 'C', label: 'Celsius (°C)' },
      { code: 'F', label: 'Fahrenheit (°F)' },
      { code: 'K', label: 'Kelvin (K)' },
    ],
  },
};

function convertTemp(v, from, to) {
  let c;
  if (from === 'C') c = v; else if (from === 'F') c = (v - 32) * 5 / 9; else c = v - 273.15;
  if (to === 'C') return c; if (to === 'F') return c * 9 / 5 + 32; return c + 273.15;
}

export function UnitConverter() {
  const tool = getToolBySlug('unit-converter');
  const [cat, setCat] = useState('length');
  const [value, setValue] = useState('1');
  const [from, setFrom] = useState('m');
  const [to, setTo] = useState('ft');

  useEffect(() => {
    const first = UNITS[cat].items[0].code;
    const second = UNITS[cat].items[1]?.code || first;
    setFrom(first); setTo(second);
  }, [cat]);

  const v = num(value);
  let out = NaN;
  if (!isNaN(v)) {
    if (cat === 'temperature') out = convertTemp(v, from, to);
    else {
      const f = UNITS[cat].items.find((i) => i.code === from);
      const t = UNITS[cat].items.find((i) => i.code === to);
      if (f && t) out = (v * f.to) / t.to;
    }
  }

  return (
    <ToolPage
      tool={tool}
      intro={<p>Convert between metric and imperial units for length, weight, volume, and temperature. Values update as you type.</p>}
      formula={<>Length/weight/volume use SI base units internally (m, kg, L). Temperature uses standard °C ↔ °F ↔ K conversions.</>}
      examples={[
        '1 meter = 3.2808 feet.',
        '1 pound ≈ 0.4536 kilograms.',
        '100 °C = 212 °F = 373.15 K.',
      ]}
      faqs={[
        { q: 'Which gallon do you use?', a: 'The US gallon (≈ 3.785 L). If you need the UK/imperial gallon (4.546 L), let us know and we\'ll add it.' },
        { q: 'Are conversions rounded?', a: 'Displayed values are rounded to 6 significant figures, but all arithmetic uses full double-precision.' },
      ]}
    >
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <Field label="Category" htmlFor="uc-cat">
            <select id="uc-cat" data-testid="uc-cat" value={cat} onChange={(e) => setCat(e.target.value)} className={inputClass}>
              {Object.entries(UNITS).map(([k, u]) => <option key={k} value={k}>{u.label}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Value" htmlFor="uc-value">
              <input id="uc-value" data-testid="uc-value" type="number" step="any" value={value} onChange={(e) => setValue(e.target.value)} className={inputClass} />
            </Field>
            <Field label="From" htmlFor="uc-from">
              <select id="uc-from" data-testid="uc-from" value={from} onChange={(e) => setFrom(e.target.value)} className={inputClass}>
                {UNITS[cat].items.map((i) => <option key={i.code} value={i.code}>{i.label}</option>)}
              </select>
            </Field>
            <Field label="To" htmlFor="uc-to">
              <select id="uc-to" data-testid="uc-to" value={to} onChange={(e) => setTo(e.target.value)} className={inputClass}>
                {UNITS[cat].items.map((i) => <option key={i.code} value={i.code}>{i.label}</option>)}
              </select>
            </Field>
          </div>
        </div>
        <div className="result-panel rounded-xl p-6">
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Converted value</div>
          <div className="text-4xl font-bold text-emerald-700" data-testid="uc-result">
            {isNaN(out) ? '—' : formatNumber(out, 6).replace(/\.?0+$/, '')}
          </div>
          <div className="mt-2 text-sm text-slate-500">{from} → {to}</div>
        </div>
      </div>
    </ToolPage>
  );
}

// ========================= Currency Converter =========================
const FALLBACK = { USD: 1, EUR: 0.92, GBP: 0.79, INR: 83.2, CAD: 1.36, AUD: 1.51, JPY: 155.5 };

export function CurrencyConverter() {
  const tool = getToolBySlug('currency-converter');
  const [amount, setAmount] = useState('100');
  const [from, setFrom] = useState('USD');
  const [to, setTo] = useState('EUR');
  const [rates, setRates] = useState(FALLBACK);
  const [updated, setUpdated] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/rates`).then((r) => {
      if (!alive) return;
      setRates(r.data.rates || FALLBACK);
      setUpdated(r.data.time_last_update_utc || null);
      setStatus('live');
    }).catch(() => { if (alive) setStatus('fallback'); });
    return () => { alive = false; };
  }, []);

  const currencies = Object.keys(rates).sort();
  const A = num(amount);
  const rFrom = rates[from], rTo = rates[to];
  const out = !isNaN(A) && rFrom && rTo ? (A / rFrom) * rTo : NaN;
  const usdToEur = rates.USD && rates.EUR ? (100 / rates.USD) * rates.EUR : NaN;
  const jpyToUsd = rates.JPY && rates.USD ? (10000 / rates.JPY) * rates.USD : NaN;
  const exampleRateLabel = status === 'live' ? 'current indicative rate' : 'fallback reference rate';

  const swap = () => { setFrom(to); setTo(from); };

  return (
    <ToolPage
      tool={tool}
      intro={<p>Convert between world currencies using indicative daily rates. Rates refresh at least once every 24 hours.</p>}
      formula={<>result = (amount / rate_from_USD) × rate_to_USD</>}
      examples={[
        `100 USD → EUR = ${formatNumber(usdToEur, 2)} EUR using the ${exampleRateLabel}.`,
        `10,000 JPY → USD = ${formatNumber(jpyToUsd, 2)} USD using the ${exampleRateLabel}.`,
      ]}
      faqs={[
        { q: 'Where do the rates come from?', a: 'Our backend proxies open.er-api.com, a free public exchange-rate API. Rates are indicative and refreshed hourly server-side.' },
        { q: 'Is this suitable for trading or remittance?', a: 'No. Retail exchange typically includes spreads and fees on top of the mid-market rate shown here. Always confirm with your bank or provider.' },
      ]}
      limitations={<>Indicative rates only. Not for trading, remittance quoting, or accounting. Real transactions include spreads, fees, and settlement delays.</>}
    >
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <Field label="Amount" htmlFor="cc-amount">
            <input id="cc-amount" data-testid="cc-amount" type="number" step="any" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputClass} />
          </Field>
          <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-end">
            <Field label="From" htmlFor="cc-from">
              <select id="cc-from" data-testid="cc-from" value={from} onChange={(e) => setFrom(e.target.value)} className={inputClass}>
                {currencies.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <button type="button" onClick={swap} data-testid="cc-swap" className="h-11 px-3 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold">⇄</button>
            <Field label="To" htmlFor="cc-to">
              <select id="cc-to" data-testid="cc-to" value={to} onChange={(e) => setTo(e.target.value)} className={inputClass}>
                {currencies.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>
          <div className="text-xs text-slate-500" data-testid="cc-status">
            {status === 'loading' ? 'Loading live rates…' : status === 'live' ? `Live rates · updated ${updated || 'recently'}` : 'Using fallback rates — live provider unavailable.'}
          </div>
        </div>
        <div className="result-panel rounded-xl p-6">
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Result</div>
          <div className="text-4xl font-bold text-emerald-700" data-testid="cc-result">
            {isNaN(out) ? '—' : `${formatNumber(out, 2)} ${to}`}
          </div>
          {!isNaN(out) && A > 0 && (
            <div className="mt-2 text-sm text-slate-600">1 {from} ≈ {formatNumber((rTo / rFrom), 4)} {to}</div>
          )}
        </div>
      </div>
    </ToolPage>
  );
}
